(() => {
  if (customElements.get('awatt-device-search')) return;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('de').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, ' ').trim();
  const compact = value => normalize(value).replace(/\s/g, '');
  class AwattDeviceSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.query = this.querySelector('[data-device-query]');
      this.category = this.querySelector('[data-device-category]');
      this.list = this.querySelector('[data-device-list]');
      this.status = this.querySelector('[data-device-status]');
      this.clear = this.querySelector('[data-device-clear]');
      this.more = this.querySelector('[data-device-more]');
      this.examples = this.querySelector('[data-device-examples]');
      this.announcement = this.querySelector('[data-device-announcement]');
      this.results = this.querySelector('[data-device-results]');
      this.limit = 6;
      this.categoryValue = 'all';
      this.querySelector('form').addEventListener('submit', event => event.preventDefault());
      this.query.addEventListener('input', () => {
        this.limit = 6;
        this.render();
      });
      this.query.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.query.value) {
          event.preventDefault();
          this.clearQuery();
        }
      });
      this.category.addEventListener('change', () => {
        this.categoryValue = this.category.value;
        this.limit = 6;
        this.render();
      });
      this.clear.addEventListener('click', () => this.clearQuery());
      this.more.addEventListener('click', () => {
        this.limit += 6;
        this.render();
        if (this.more.hidden) {
          this.status.setAttribute('tabindex', '-1');
          this.status.focus();
        }
      });
      this.load();
    }
    async load() {
      this.results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Unavailable device data');
        const data = await response.json();
        if (!Array.isArray(data.manufacturers) || !Array.isArray(data.categories)) throw new Error('Invalid device data');
        this.data = data;
        this.prepareRecords();
        data.categories.forEach(category => {
          const option = document.createElement('option');
          option.value = category.id;
          option.textContent = category.title;
          this.category.append(option);
        });
        this.category.disabled = false;
        this.querySelector('[data-device-source]').textContent = `${data.labels.source} ${data.source.dateLabel} · evcc ${data.source.version}`;
        const label = document.createElement('span');
        label.textContent = data.labels.examples;
        this.examples.append(label);
        data.examples.forEach(example => {
          if (!data.manufacturers.some(record => record.name === example)) return;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'awatt-device-search__chip';
          button.textContent = example;
          button.addEventListener('click', () => {
            this.query.value = example;
            this.category.value = 'all';
            this.categoryValue = 'all';
            this.limit = 6;
            this.render();
            this.query.focus();
          });
          this.examples.append(button);
        });
        this.render(false);
      } catch (error) {
        this.status.textContent = 'Die Schnellsuche ist gerade nicht verfügbar. Öffne die Herstellerübersicht unten.';
        this.category.disabled = true;
      } finally {
        this.results.setAttribute('aria-busy', 'false');
      }
    }
    prepareRecords() {
      this.records = this.data.manufacturers.map(record => {
        const categories = record.categories.map(id => this.data.categories.find(category => category.id === id)).filter(Boolean);
        const names = [record.name, ...(record.aliases || [])].flatMap(name => [name, name.replace(/ä/gi, 'ae').replace(/ö/gi, 'oe').replace(/ü/gi, 'ue')]);
        return { ...record, categoryRecords: categories, names: names.map(normalize), compactNames: names.map(compact) };
      });
    }
    findMatches(value, categoryValue = 'all') {
      const search = normalize(value);
      if (!search) return [];
      const compactSearch = compact(value);
      return (this.records || []).filter(record => {
        if (categoryValue !== 'all' && !record.categories.includes(categoryValue)) return false;
        return record.names.some(name => name.startsWith(search) || name.includes(` ${search}`)) || record.compactNames.some(name => name.startsWith(compactSearch));
      }).sort((a, b) => {
        const rank = record => record.names.includes(search) || record.compactNames.includes(compactSearch) ? 0 : record.names.some(name => name.startsWith(search)) || record.compactNames.some(name => name.startsWith(compactSearch)) ? 1 : 2;
        return rank(a) - rank(b) || a.name.localeCompare(b.name, 'de');
      });
    }
    clearQuery() {
      this.query.value = '';
      this.limit = 6;
      this.render();
      this.query.focus();
    }
    announce(message) {
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        if (this.announcement.textContent !== message) this.announcement.textContent = message;
      }, 350);
    }
    render(announce = true) {
      this.clear.hidden = !this.query.value;
      if (!this.data) return;
      const value = this.query.value.trim();
      const labels = this.data.labels;
      if (!normalize(value)) {
        this.status.textContent = labels.empty;
        this.list.hidden = true;
        this.list.replaceChildren();
        this.more.hidden = true;
        if (announce) this.announce(labels.empty);
        return;
      }
      const matches = this.findMatches(value, this.categoryValue);
      this.list.hidden = !matches.length;
      this.list.replaceChildren(...matches.slice(0, this.limit).map(record => this.makeResult(record)));
      this.status.replaceChildren();
      if (matches.length) {
        this.status.textContent = `${matches.length} ${matches.length === 1 ? labels.match : labels.matches} ${labels.forQuery} „${value}“`;
      } else {
        const text = document.createElement('span');
        text.textContent = `${labels.noResult} „${value}“. ${labels.noResultNote} `;
        const link = document.createElement('a');
        link.href = this.dataset.contactUrl || '/pages/contact';
        link.textContent = labels.contact;
        this.status.append(text, link);
      }
      this.more.hidden = matches.length <= this.limit;
      this.more.textContent = `${labels.more} (${Math.min(6, Math.max(0, matches.length - this.limit))})`;
      if (announce) this.announce(this.status.textContent);
    }
    makeResult(record) {
      const labels = this.data.labels;
      const item = document.createElement('li');
      item.className = 'awatt-device-search__result';
      const top = document.createElement('div');
      top.className = 'awatt-device-search__result-top';
      const name = document.createElement('h3');
      name.className = 'awatt-device-search__name';
      name.textContent = record.name;
      const listed = document.createElement('span');
      listed.className = 'awatt-device-search__listed';
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tick.setAttribute('viewBox', '0 0 16 16');
      tick.setAttribute('aria-hidden', 'true');
      tick.setAttribute('focusable', 'false');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm3 8 3 3 7-7');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.4');
      tick.append(path);
      listed.append(tick, document.createTextNode(labels.listed));
      top.append(name, listed);
      const badges = document.createElement('div');
      badges.className = 'awatt-device-search__badges';
      const categories = record.categoryRecords.filter(category => this.categoryValue === 'all' || category.id === this.categoryValue);
      categories.forEach(category => {
        const badge = document.createElement('span');
        badge.className = `awatt-device-search__badge${category.status ? ' awatt-device-search__badge--trial' : ''}`;
        badge.textContent = category.status ? `${category.title} · ${category.status}` : category.title;
        badges.append(badge);
      });
      const note = document.createElement('p');
      note.className = 'awatt-device-search__result-note';
      note.textContent = categories.some(category => category.status) ? labels.trialNote : labels.modelNote;
      item.append(top, badges, note);
      return item;
    }
  }
  customElements.define('awatt-device-search', AwattDeviceSearch);
})();
